/**
 * youtubePublishApi — single source of truth for all YouTube Publish API v3
 * endpoints mounted under /api/v1/youtube.
 *
 * Auth: none of these endpoints REQUIRE a Firebase ID token to function, but
 * submitPublishJob() sends one when available (getAuthHeaders()) so the
 * backend can bill the SEO localizer's LLM calls server-side via Cloud Run
 * on desktop instead of needing a local OPENROUTER_API_KEY — see
 * youtube_publish.seo_localizer._build_llm_client. A missing/failed token
 * degrades to the local-key path rather than failing the request.
 * Use getApiBaseUrl() because /youtube/* is registered on both the desktop
 * api_server (localhost) and Cloud Run — no buildCloudApiUrl needed.
 */

import { getApiBaseUrl } from './urlResolver';
import { handleDirectResponse, handleResponse, JSON_HEADERS } from '../utils/httpUtils';
import { firebaseClient } from './firebase/firebase-client';
import type {
  YouTubePublishRequest,
  YouTubePublishJobStatus,
} from '../types/generated/youtubePublish';

/**
 * Bearer-only auth header (NO Content-Type / X-Requested-With).
 *
 * Intentionally NOT getAuthHeadersFromStorage(): several endpoints here are
 * bodyless GETs (channels/videos/quota/...). Adding Content-Type to a GET turns
 * it into a CORS-preflighted request, which intermittently fails on cold Cloud
 * Run instances. The POST handlers add JSON_HEADERS explicitly where a body
 * exists. Do not "dedup" this into the shared full-headers helper.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await firebaseClient.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Re-export generated types so callers only need this one import.
// ---------------------------------------------------------------------------
export type {
  YouTubePublishRequest,
  YouTubePublishJobStatus,
  YouTubePublishMode,
  YouTubeNarrationSource,
  YouTubeLanguageConfig,
  YouTubeUploadMetadata,
} from '../types/generated/youtubePublish';

// ---------------------------------------------------------------------------
// Types exclusive to the OAuth + publish v3 layer (not in generated schema)
// ---------------------------------------------------------------------------

export interface OAuthConfiguredResponse {
  configured: boolean;
}

export interface OAuthStartResponse {
  auth_url: string;
  state: string;
  port: number;
}

export type OAuthStatusStatus = 'pending' | 'completed' | 'failed';

export interface OAuthStatusResponse {
  status: OAuthStatusStatus;
  channel_id?: string;
  channel_title?: string;
  error?: string;
}

export interface YouTubeProfile {
  channel_id: string;
  channel_title: string;
  channel_handle: string;
  email: string;
  connected_at: string;
}

export interface YouTubeProfilesResponse {
  profiles: YouTubeProfile[];
}

export interface YouTubeChannel {
  id: string;
  title: string;
  thumbnail_url: string;
}

export interface YouTubeChannelsResponse {
  channels: YouTubeChannel[];
}

export interface YouTubeChannelVideo {
  video_id: string;
  title: string;
  description: string;
  thumbnail_url: string;
  published_at: string;
  url: string;
  /** Total video length in seconds (0 when unavailable). */
  duration_seconds: number;
  /** BCP-47 declared audio language (empty when the video declares none). */
  default_audio_language: string;
  /** BCP-47 declared metadata language (empty when the video declares none). */
  default_language: string;
}

export interface ChannelVideosResponse {
  videos: YouTubeChannelVideo[];
  next_page_token: string | null;
}

/** Which languages already exist on a video (so the user fills only the gaps). */
export interface VideoLanguageStatus {
  captions: string[];
  localizations: string[];
  default_language: string;
}

export interface WorkingDirResponse {
  working_dir: string;
}

export interface MetadataFromVideoBody {
  channel_id: string;
  video_url: string;
  source_language?: string | null;
  language?: string | null;
}

export interface MetadataFromVideoStartResponse {
  job_id: string;
  estimated_tokens: number;
}

export interface MetadataFromVideoResult {
  title: string;
  title_alternatives: string[];
  description: string;
  tags: string[];
  hashtags: string[];
  detected_language: string;
}

/** SSE event shape for the AI-from-video metadata job. */
export interface MetadataFromVideoEvent {
  type: 'progress' | 'completed' | 'failed';
  stage?: string;
  message?: string;
  result?: MetadataFromVideoResult;
  error?: string;
}

export interface SubmitPublishJobBody {
  channel_id: string;
  /**
   * Optional run/assets folder. When present it enables pipeline_generated
   * captions (subtitles_<lang>.srt) and SEO-from-narration (video_project.json).
   * For end_to_end it is the generation folder; for edit_existing it is the
   * minted session dir used when a thumbnail was generated before submit. When
   * omitted, the backend derives it from the uploaded video's parent folder for
   * end_to_end, else mints an empty temp dir.
   */
  job_assets_root?: string;
  request: YouTubePublishRequest;
}

export interface SubmitPublishJobResponse {
  job_id: string;
  estimated_units: number;
  deduped?: boolean;
}

export interface YouTubeQuotaTransaction {
  timestamp: string;
  endpoint: string;
  cost: number;
}

export interface YouTubeQuotaSnapshot {
  channel_id: string;
  consumed: number;
  daily_limit: number;
  remaining: number;
  reset_at: string;
  transactions: YouTubeQuotaTransaction[];
}

export interface YouTubeLanguageEntry {
  code: string;
  name_en: string;
  name_pt: string;
  native_name: string;
  region: string;
}

export interface YouTubeLanguagesResponse {
  languages: YouTubeLanguageEntry[];
}

// ---------------------------------------------------------------------------
// Thumbnail API (shared/routes/ai_generation.py)
// ---------------------------------------------------------------------------

export interface SaveThumbnailToJobAssetsBody {
  job_assets_root: string;
  language_code: string;
  // Exactly one of the three image sources must be provided:
  image_data_url?: string;
  image_path?: string;
  image_url?: string;
}

export interface SaveThumbnailToJobAssetsResponse {
  ok: boolean;
  saved_path: string;
}

export interface ClearThumbnailFromJobAssetsBody {
  job_assets_root: string;
  language_code: string;
}

export interface ClearThumbnailFromJobAssetsResponse {
  ok: boolean;
  /** True when a file was actually deleted; false when none existed (idempotent). */
  removed: boolean;
  cleared_path: string;
}

/**
 * Request body for POST /api/v1/thumbnail/ai/generate.
 *
 * Field names match the backend Pydantic model `GenerateRequest` exactly
 * (snake_case, per RULE #1.5). Mirrors shared/routes/ai_generation.py
 * `GenerateRequest` (lines ~73-100) and `AIGenerationPreferencesModel`.
 */
export interface GenerateThumbnailPreferences {
  mood?: string;
  thumbnail_style?: string;
  text_position?: string;
  /** Fooocus style names to force (maps from the StyleSelector multi-select). */
  fooocus_style_override?: string[];
  include_face?: boolean;
  include_logo?: boolean;
  color_scheme_override?: string[];
}

export interface GenerateThumbnailBody {
  title: string;
  description?: string;
  variation_count?: number;
  format_preset?: string;
  /** Image provider id (e.g. fooocus, seedream-v4-t2i). */
  provider?: string;
  /** Provider-specific checkpoint/model name. */
  model?: string;
  /** Explicit width override (paired with height, overrides format_preset). */
  width?: number;
  /** Explicit height override (paired with width, overrides format_preset). */
  height?: number;
  /**
   * Overlay the title text on the image. Omit/true = current behaviour; false =
   * purely visual, text-free thumbnail (multilingual publish: one thumbnail for
   * all languages, so single-language title text would be wrong for the rest).
   */
  text_overlay?: boolean;
  /**
   * User-authored image prompt. When set, it drives the thumbnail concept
   * verbatim and the backend SKIPS the LLM title analysis. When omitted, the
   * concept is derived from the title/description (default behaviour).
   */
  custom_prompt?: string;
  /** Optional negative prompt; only honored by the backend alongside custom_prompt. */
  custom_negative_prompt?: string;
  preferences?: GenerateThumbnailPreferences;
}

/**
 * Single variation in the /generate response.
 *
 * The backend serialises via ThumbnailVariation.to_dict() which uses camelCase
 * keys (imageUrl, thumbnailUrl, canvasState, metadata).
 * Field name confirmed from marketing/generators/thumbnail/ai/auto_generator.py
 * line 92: `"imageUrl": self.image_url`.
 */
export interface GenerateThumbnailVariation {
  id: string;
  index: number;
  /** Local filesystem path OR HTTPS URL depending on the active image provider. */
  imageUrl: string;
  thumbnailUrl: string;
  metadata: Record<string, unknown>;
}

export interface GenerateThumbnailResult {
  id: string;
  status: string;
  variations: GenerateThumbnailVariation[];
}

/**
 * Envelope returned by handleResponse for /generate.
 *
 * success_response wraps data as { success, data: { result: ... } }.
 * handleResponse unwraps the outer `data` key, so the caller receives
 * { result: GenerateThumbnailResult }.
 */
export interface GenerateThumbnailResponse {
  result: GenerateThumbnailResult;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function base(): string {
  return getApiBaseUrl();
}

const YT = '/api/v1/youtube';

// ---------------------------------------------------------------------------
// OAuth
// ---------------------------------------------------------------------------

export async function isOAuthConfigured(): Promise<boolean> {
  const resp = await fetch(`${base()}${YT}/oauth/configured`);
  const data = await handleDirectResponse<OAuthConfiguredResponse>(resp);
  return data.configured;
}

export async function startOAuth(): Promise<OAuthStartResponse> {
  const resp = await fetch(`${base()}${YT}/oauth/start`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: '{}',
  });
  return handleDirectResponse<OAuthStartResponse>(resp);
}

export async function pollOAuthStatus(state: string): Promise<OAuthStatusResponse> {
  const resp = await fetch(`${base()}${YT}/oauth/status?state=${encodeURIComponent(state)}`);
  return handleDirectResponse<OAuthStatusResponse>(resp);
}

export async function logoutOAuth(channel_id: string): Promise<void> {
  const resp = await fetch(`${base()}${YT}/oauth/logout`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: JSON.stringify({ channel_id }),
  });
  await handleDirectResponse<unknown>(resp);
}

// ---------------------------------------------------------------------------
// Profiles & Channels
// ---------------------------------------------------------------------------

export async function listProfiles(): Promise<YouTubeProfile[]> {
  const resp = await fetch(`${base()}${YT}/profiles`, {
    headers: await getAuthHeaders(),
  });
  const data = await handleDirectResponse<YouTubeProfilesResponse>(resp);
  return data.profiles;
}

export async function listChannels(channel_id: string): Promise<YouTubeChannel[]> {
  const resp = await fetch(`${base()}${YT}/channels?channel_id=${encodeURIComponent(channel_id)}`, {
    headers: await getAuthHeaders(),
  });
  const data = await handleDirectResponse<YouTubeChannelsResponse>(resp);
  return data.channels;
}

/**
 * List one page of the connected channel's own uploaded videos.
 *
 * Powers the "Edit existing video" picker. Pass the `next_page_token` from a
 * previous call to fetch the next page (button-based pagination).
 */
export async function listChannelVideos(
  channel_id: string,
  pageToken?: string | null,
  query?: string | null
): Promise<ChannelVideosResponse> {
  const params = new URLSearchParams({ channel_id });
  if (pageToken) {
    params.set('page_token', pageToken);
  }
  if (query && query.trim()) {
    params.set('q', query.trim());
  }
  const resp = await fetch(`${base()}${YT}/videos?${params.toString()}`, {
    headers: await getAuthHeaders(),
  });
  return handleDirectResponse<ChannelVideosResponse>(resp);
}

/**
 * Report which target languages already have captions / SEO localizations on a
 * video, so the publish form can badge them and let the user focus on the gaps.
 */
export async function getVideoLanguageStatus(
  channel_id: string,
  video_url: string
): Promise<VideoLanguageStatus> {
  const params = new URLSearchParams({ channel_id, video_url });
  const resp = await fetch(`${base()}${YT}/video-language-status?${params.toString()}`, {
    headers: await getAuthHeaders(),
  });
  return handleDirectResponse<VideoLanguageStatus>(resp);
}

/**
 * Mint a server-side working directory for the publish session. Used by the
 * thumbnail step so the user never types a filesystem path; the returned dir is
 * passed back as job_assets_root on submit.
 */
export async function createPublishWorkingDir(): Promise<WorkingDirResponse> {
  const resp = await fetch(`${base()}${YT}/session/working-dir`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: '{}',
  });
  return handleDirectResponse<WorkingDirResponse>(resp);
}

/**
 * Start an async job that writes SEO title/description from a video's audio
 * (download -> speech-to-text -> SEO). Subscribe to events via
 * subscribeToMetadataFromVideoEvents() for progress and the result.
 */
export async function generateMetadataFromVideo(
  body: MetadataFromVideoBody
): Promise<MetadataFromVideoStartResponse> {
  const resp = await fetch(`${base()}${YT}/metadata/from-video`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: JSON.stringify(body),
  });
  return handleDirectResponse<MetadataFromVideoStartResponse>(resp);
}

/**
 * Subscribe to SSE events for an AI-from-video metadata job.
 * Returns a close() function the caller must invoke on unmount.
 */
export function subscribeToMetadataFromVideoEvents(
  job_id: string,
  onEvent: (event: MetadataFromVideoEvent) => void
): () => void {
  const url = `${base()}${YT}/metadata/from-video/${encodeURIComponent(job_id)}/events`;
  const source = new EventSource(url);
  source.onmessage = (e: MessageEvent) => {
    try {
      onEvent(JSON.parse(e.data) as MetadataFromVideoEvent);
    } catch {
      // Ignore malformed frames.
    }
  };
  return () => source.close();
}

// ---------------------------------------------------------------------------
// Publish jobs
// ---------------------------------------------------------------------------

export async function submitPublishJob(
  body: SubmitPublishJobBody
): Promise<SubmitPublishJobResponse> {
  const resp = await fetch(`${base()}${YT}/publish`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: JSON.stringify(body),
  });
  return handleDirectResponse<SubmitPublishJobResponse>(resp);
}

export async function getPublishJob(job_id: string): Promise<YouTubePublishJobStatus> {
  const resp = await fetch(`${base()}${YT}/publish/${encodeURIComponent(job_id)}`);
  return handleDirectResponse<YouTubePublishJobStatus>(resp);
}

export async function cancelPublishJob(job_id: string): Promise<void> {
  const resp = await fetch(`${base()}${YT}/publish/${encodeURIComponent(job_id)}/cancel`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: '{}',
  });
  await handleDirectResponse<unknown>(resp);
}

/**
 * Subscribe to SSE events for a publish job.
 *
 * Returns a close() function. The caller is responsible for calling it on
 * unmount. Uses native EventSource — no polling fallback.
 */
export function subscribeToJobEvents(
  job_id: string,
  onEvent: (event: MessageEvent) => void
): () => void {
  const url = `${base()}${YT}/publish/${encodeURIComponent(job_id)}/events`;
  const source = new EventSource(url);
  source.onmessage = onEvent;
  return () => source.close();
}

// ---------------------------------------------------------------------------
// Quota
// ---------------------------------------------------------------------------

export async function getQuota(channel_id: string): Promise<YouTubeQuotaSnapshot> {
  const resp = await fetch(`${base()}${YT}/quota?channel_id=${encodeURIComponent(channel_id)}`, {
    headers: await getAuthHeaders(),
  });
  return handleDirectResponse<YouTubeQuotaSnapshot>(resp);
}

// ---------------------------------------------------------------------------
// Languages / SEO / Metadata (pre-existing, integrated here)
// ---------------------------------------------------------------------------

export async function getLanguages(): Promise<YouTubeLanguageEntry[]> {
  const resp = await fetch(`${base()}${YT}/languages`);
  const data = await handleDirectResponse<YouTubeLanguagesResponse>(resp);
  return data.languages;
}

export async function saveThumbnailToJobAssets(
  body: SaveThumbnailToJobAssetsBody
): Promise<SaveThumbnailToJobAssetsResponse> {
  const resp = await fetch(`${base()}/api/v1/thumbnail/ai/save-to-job-assets`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  // Backend wraps the payload in { success, data: {...} } via success_response();
  // handleResponse unwraps `data` (handleDirectResponse would leave saved_path undefined).
  return handleResponse<SaveThumbnailToJobAssetsResponse>(resp);
}

/**
 * Remove a previously saved thumbnail from the job assets dir (deselect).
 *
 * The inverse of saveThumbnailToJobAssets — deletes
 * <job_assets_root>/thumbnails/<language_code>.png so a deselected thumbnail is
 * genuinely not uploaded (the publish orchestrator uploads any file it finds at
 * that path). Idempotent: removing an absent file resolves with removed=false.
 */
export async function clearThumbnailFromJobAssets(
  body: ClearThumbnailFromJobAssetsBody
): Promise<ClearThumbnailFromJobAssetsResponse> {
  const resp = await fetch(`${base()}/api/v1/thumbnail/ai/clear-from-job-assets`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(body),
  });
  // Backend wraps in { success, data: {...} } via success_response(); unwrap with handleResponse.
  return handleResponse<ClearThumbnailFromJobAssetsResponse>(resp);
}

/**
 * Call POST /api/v1/thumbnail/ai/generate to produce one or more thumbnail
 * variations for the given title/description.
 *
 * Uses handleResponse (not handleDirectResponse) because the backend wraps
 * the payload in { success, data: { result: ... } } via success_response().
 */
export async function generateThumbnail(
  body: GenerateThumbnailBody
): Promise<GenerateThumbnailResponse> {
  const resp = await fetch(`${base()}/api/v1/thumbnail/ai/generate`, {
    method: 'POST',
    // Authorization is REQUIRED here, unlike the desktop-local thumbnail
    // save/clear calls above: POST /api/v1/thumbnail/ai/generate is a BILLED
    // operation (constants/pricing/billed_routes.py). Off-cloud the backend
    // forwards this ID token to Cloud Run to reserve the tokens, and with no
    // token it refuses the work rather than run cloud compute for free
    // (BillingAuthenticationRequiredError -> 401). Sending JSON_HEADERS alone
    // made every AI thumbnail generation on the desktop an unauthenticated one.
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: JSON.stringify(body),
  });
  return handleResponse<GenerateThumbnailResponse>(resp);
}

// ---------------------------------------------------------------------------
// Web OAuth flow (Cloud Run / Vite dev) — requires Firebase auth
// ---------------------------------------------------------------------------

export interface StartWebOAuthResponse {
  auth_url: string;
  state: string;
}

export interface ExchangeOAuthResponse {
  channel_id: string;
  channel_title: string;
  channel_handle: string | null;
  email: string;
}

export async function startWebOAuth(redirectUri: string): Promise<StartWebOAuthResponse> {
  const resp = await fetch(`${base()}${YT}/oauth/start-web`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: JSON.stringify({ redirect_uri: redirectUri }),
  });
  return handleDirectResponse<StartWebOAuthResponse>(resp);
}

export async function exchangeOAuthCode(
  code: string,
  state: string
): Promise<ExchangeOAuthResponse> {
  const resp = await fetch(`${base()}${YT}/oauth/exchange`, {
    method: 'POST',
    headers: { ...JSON_HEADERS, ...(await getAuthHeaders()) },
    body: JSON.stringify({ code, state }),
  });
  return handleDirectResponse<ExchangeOAuthResponse>(resp);
}

// ---------------------------------------------------------------------------
// Publish from a finished generation (the bridge)
//
// Resolve a completed local video-generation run (temp/<run_id>/) into the
// assets the publish form needs, so the user can pick a generation instead of
// re-typing paths / re-generating SEO.
// ---------------------------------------------------------------------------

export interface RecentGeneration {
  run_id: string;
  run_dir: string;
  title: string;
  language: string;
  subtitle_languages: string[];
  has_video: boolean;
  video_path: string | null;
  has_seo: boolean;
  mtime: number;
}

export interface GenerationValidation {
  valid_for_publish: boolean;
  valid_for_seo: boolean;
  errors: string[];
  warnings: string[];
  detected: Record<string, unknown>;
}

export interface GenerationManifest {
  run_id: string;
  run_dir: string;
  video_path: string | null;
  source_language: string;
  title: string;
  // null when the run persisted no base description (backend generates it from
  // narration at publish time). Mirrors GenerationManifest.description: str | None.
  description: string | null;
  available_languages: string[];
  captions: Record<string, string>;
  localizations: Record<string, { title: string; description: string }>;
  thumbnails: Record<string, string>;
  has_seo: boolean;
  tags: string[];
  validation: GenerationValidation;
}

/** List finished local generations (newest first) for the publish picker. */
export async function listRecentGenerations(limit = 50): Promise<RecentGeneration[]> {
  const resp = await fetch(`${base()}${YT}/generation/recent?limit=${limit}`);
  const data = await handleDirectResponse<{ generations: RecentGeneration[] }>(resp);
  return data.generations;
}

/** Resolve one finished generation folder into a full publish manifest. */
export async function getGenerationManifest(runDir: string): Promise<GenerationManifest> {
  const resp = await fetch(
    `${base()}${YT}/generation/manifest?run_dir=${encodeURIComponent(runDir)}`
  );
  return handleDirectResponse<GenerationManifest>(resp);
}
